import { getXsrfHeaders } from '@/lib/xsrf';

/**
 * Error thrown by `apiFetch` for any non-2xx response.
 *
 * `message` is the server-provided `{ message }` when the body is JSON and
 * carries one, otherwise the caller-provided fallback (or a generic one).
 * `status` lets callers branch on specific codes (409 = already running,
 * 402 = quota, 422 = validation, ...). `body` is the parsed JSON payload when
 * there was one, so callers can read validation `errors` etc.
 */
export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(status: number, message: string, body: unknown = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export function isApiError(err: unknown): err is ApiError {
    return err instanceof ApiError;
}

/**
 * What Laravel's exception handler writes into `{ message }` on its own in
 * production (`APP_DEBUG=false`): English, sometimes naming an Eloquent model,
 * never an answer the app composed for the user. Read as "no message", so the
 * caller's Portuguese fallback shows instead.
 */
const FRAMEWORK_MESSAGES: RegExp[] = [
    /^Server Error$/,
    /^Service Unavailable$/,
    /^No query results for model /,
    /^This action is unauthorized\.$/,
    /^Unauthenticated\.$/,
    /^CSRF token mismatch\.$/,
    /^Too Many Attempts\.$/,
    /^(Not found|Bad request)\.$/,
    /^The route .* could not be found\.$/,
    /^The \w+ method is not supported for route /,
];

export function extractMessage(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') return null;
    const message = (payload as { message?: unknown }).message;
    if (typeof message !== 'string' || message.trim() === '') return null;
    return FRAMEWORK_MESSAGES.some((pattern) => pattern.test(message))
        ? null
        : message;
}

/**
 * Server-provided messages Laravel emits before the app's own controllers get
 * a chance to (auth middleware, the throttle middleware): always English,
 * never meant for an end user. Replaced with a Portuguese message keyed on
 * status; every other status keeps the `{ message }` the app itself wrote.
 */
const STATUS_MESSAGE_OVERRIDES: Partial<Record<number, string>> = {
    401: 'Sessão expirada. Faça login novamente.',
    419: 'Sessão expirada. Faça login novamente.',
    429: 'Muitas tentativas. Aguarde um instante e tente novamente.',
};

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
    /** JSON-serialised into the request body. */
    json?: unknown;
    /** Raw body (FormData, string, ...). Ignored when `json` is given. */
    body?: BodyInit | null;
    /** Message used when the server did not provide one. */
    errorMessage?: string;
}

/**
 * Same-origin JSON fetch with Laravel's XSRF header attached.
 *
 * Resolves with the parsed JSON body (or `null` for an empty body) on 2xx and
 * throws `ApiError` otherwise. Network failures propagate as the underlying
 * `TypeError` so callers/TanStack can tell "server said no" from "no server".
 */
export async function apiFetch<T = unknown>(
    url: string,
    { json, body, errorMessage, headers, ...init }: ApiFetchOptions = {},
): Promise<T> {
    // Built as a real `Headers` object (not a plain record spread) so a
    // caller-supplied `Headers` instance or a `[key, value][]` tuple array is
    // honoured instead of silently dropped/mangled.
    const requestHeaders = new Headers(getXsrfHeaders());
    if (headers) {
        new Headers(headers).forEach((value, key) => {
            requestHeaders.set(key, value);
        });
    }
    // getXsrfHeaders() always sets Content-Type: application/json. A raw
    // FormData body needs the multipart boundary the browser generates for
    // it instead — forcing JSON here would make the server unable to parse
    // the body at all.
    if (json === undefined && body instanceof FormData) {
        requestHeaders.delete('Content-Type');
    }

    const response = await fetch(url, {
        credentials: 'same-origin',
        ...init,
        headers: requestHeaders,
        body: json !== undefined ? JSON.stringify(json) : body,
    });

    return handleResponse<T>(response, errorMessage);
}

/**
 * Turn a raw `Response` into the parsed body or an `ApiError`.
 *
 * `response.text()` is awaited without a try/catch: a rejection (connection
 * dropped mid-body) propagates same as a fetch `TypeError`, so callers/
 * TanStack can still tell "server said no" from "no server". An empty body
 * resolves to `null` on 2xx. A non-empty body that fails to parse as JSON is
 * an `ApiError` either way — on 2xx it would otherwise resolve `null` and
 * hide the truncated/malformed response as a successful empty result.
 */
async function handleResponse<T = unknown>(
    response: Response,
    errorMessage = 'Erro ao comunicar com o servidor.',
): Promise<T> {
    const text = await response.text();

    let payload: unknown = null;
    let malformed = false;
    if (text !== '') {
        try {
            payload = JSON.parse(text) as unknown;
        } catch {
            malformed = true;
        }
    }

    const overrideMessage = STATUS_MESSAGE_OVERRIDES[response.status];

    if (!response.ok) {
        throw new ApiError(
            response.status,
            overrideMessage ?? extractMessage(payload) ?? errorMessage,
            payload,
        );
    }

    if (malformed) {
        throw new ApiError(response.status, errorMessage, null);
    }

    return payload as T;
}

/**
 * User-facing message for a failed request. Only an `ApiError` carries text
 * meant for the user; anything else is a `TypeError` from `fetch`, worded by
 * the browser ("Failed to fetch", "Load failed").
 */
export function errorMessageFrom(err: unknown, fallback: string): string {
    return isApiError(err) ? err.message : fallback;
}
